import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface VerifyCodeFormProps {
  /** Optional initial value (e.g. the failed code on NotFoundState). */
  initialCode?: string;
  /** Override placeholder for context-specific hints. */
  placeholder?: string;
  /** Eyebrow label above the input. */
  label?: string;
  /** Submit button text. */
  submitLabel?: string;
  /** Whether the input should autofocus on mount. */
  autoFocus?: boolean;
  /** Optional helper text rendered under the input. */
  helperText?: React.ReactNode;
}

/**
 * Reusable verify-code input + submit. Same controlled-input + navigate
 * behavior as the original inline form in HomePage.
 */
export function VerifyCodeForm({
  initialCode = '',
  placeholder = 'BG-BOOTCAMP-COBOG20-XXXXXX-XXXX',
  label = 'Código verificable',
  submitLabel = 'Verificar',
  autoFocus = false,
  helperText,
}: VerifyCodeFormProps) {
  const [code, setCode] = useState(initialCode);
  const navigate = useNavigate();

  // Strip ALL whitespace (not just trim) — PDF copy-paste often yields
  // "D I P - C O B O G 2 0 - 5 C B C B A - O Q 6 Z" with spaces between
  // every glyph. The backend also normalizes defensively, but doing it
  // here means the URL we navigate to is already clean.
  const normalize = (s: string) => s.replace(/\s+/g, '').toUpperCase();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = normalize(code);
    if (cleaned.length >= 8) navigate(`/${cleaned}`);
  };

  const canSubmit = normalize(code).length >= 8;

  return (
    <form onSubmit={onSubmit}>
      <label htmlFor="code-input" className="eyebrow block mb-3">
        {label}
      </label>
      <div className="relative">
        <input
          id="code-input"
          type="text"
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="w-full bg-transparent border-b border-[var(--bi-oscuro)] focus:border-[var(--bi-energico)] outline-none font-mono text-[15px] sm:text-base px-0 py-3 pr-28 transition-colors placeholder:text-[var(--bi-claro)]/60 text-[var(--bi-oscuro)]"
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className="btn-verify absolute right-0 top-1/2 -translate-y-1/2 px-4 py-2 font-semibold text-[13px] tracking-wide rounded-md disabled:cursor-not-allowed"
          style={{
            background: canSubmit ? 'var(--bi-oscuro)' : 'transparent',
            color: canSubmit ? '#fff' : 'var(--bi-claro)',
            border: canSubmit ? '1px solid var(--bi-oscuro)' : '1px solid var(--border)',
          }}
        >
          {submitLabel}
        </button>
      </div>
      {helperText && (
        <p className="text-[11px] text-[var(--bi-claro)] mt-3">{helperText}</p>
      )}
    </form>
  );
}
